/**
 * Fishing client (I8b): E near a fish-spot casts, a bobber dips on the bite tell, E in the 0.3 s window strikes.
 * Sends fishCast / fishStrike and reads the fishBite events. Placeholder visuals: a small DOM bobber and Thai text.
 */
import type { PlayerCommand, World, WorldEvent } from '@nine/shared';

const NAMES: Record<string, string> = { fish: 'ปลา', 'rare-fish': 'ปลาหายาก' };

export class Fishing {
  private el: HTMLDivElement;
  private auto: HTMLButtonElement;
  private autoOn = false; // อัตโนมัติ: repeat loop (systems/autoGather.ts)
  private state: 'idle' | 'wait' | 'tell' = 'idle';
  private hide = 0;

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    this.el = document.createElement('div');
    this.el.style.cssText =
      'position:fixed;left:50%;bottom:22%;transform:translateX(-50%);padding:6px 14px;background:#000a;color:#ffe9a8;' +
      'border:2px solid #c9a04a;font:16px sans-serif;z-index:60;pointer-events:none;display:none';
    document.body.appendChild(this.el);
    this.auto = document.createElement('button');
    this.auto.style.cssText =
      'position:fixed;left:50%;bottom:16%;transform:translateX(-50%);padding:4px 12px;background:#000c;color:#ffe9a8;' +
      'border:2px solid #c9a04a;font:15px sans-serif;z-index:61;display:none;cursor:pointer';
    this.auto.onclick = () => this.send({ t: 'autoGather', on: !this.autoOn });
    document.body.appendChild(this.auto);
  }

  /** E pressed: true when fishing took the key. */
  key(): boolean {
    if (this.state !== 'idle') {
      this.send({ t: 'fishStrike' });
      return true;
    }
    if (this.world.trades.nodeNear(this.world.player)?.kind.id !== 'fish-spot') return false;
    this.send({ t: 'fishCast' });
    return true;
  }

  private show(text: string, bold = false, secs = 0): void {
    this.el.textContent = text;
    this.el.style.display = '';
    this.el.style.fontWeight = bold ? '700' : '400';
    this.el.style.transform = `translateX(-50%) translateY(${bold ? 10 : 0}px)`;
    this.hide = secs;
  }

  handle(ev: WorldEvent): void {
    if ((ev.type as string) === 'autoGather') this.autoOn = (ev as { on: boolean }).on;
    if (ev.type !== 'fishBite') return;
    if (ev.phase === 'cast') {
      this.state = 'wait';
      this.show('◦ ลอยเบ็ด… รอปลากิน');
    } else if (ev.phase === 'tell') {
      this.state = 'tell';
      this.show('● ทุ่นจม! กด E!', true);
    } else if (ev.phase === 'result') {
      this.state = 'idle';
      if (ev.result === 'miss') return this.show('หลุด', false, 1.5);
      const got = (ev.goods ?? []).map((g) => `${NAMES[g.id] ?? g.id} ×${g.n}`).join(' ');
      this.show(ev.result === 'perfect' ? `ปลาหายาก ×2 ${got}` : `ได้ปลา ${got}`, false, 2);
    }
  }

  sync(dt: number): void {
    const near = this.world.trades.nodeNear(this.world.player)?.kind.id === 'fish-spot';
    this.auto.style.display = near || this.autoOn ? '' : 'none';
    this.auto.textContent = this.autoOn ? 'อัตโนมัติ: เปิด' : 'อัตโนมัติ';
    if (this.hide > 0 && (this.hide -= dt) <= 0) this.el.style.display = 'none';
  }
}
